import { useMemo, useState } from 'react';
import { PARAM_LABELS, PERCENT_CODES, PLASTERING_CODES } from '../../constants/parameters.constants';
import { useTableList } from '../../hooks/useTableList';
import type { ReferenceSectionProps } from '../../types/parameters.types';
import { formatNumber } from '../../utils/format';
import { ValuesFormModal } from '../Forms/ValuesFormModal';
import { type ReferenceColumn } from '../Tables/ReferenceTable';
import { SectionList } from '../Tables/SectionList';
import tableStyles from '../Tables/ParamsTable.module.css';

interface PlasteringRow {
	code: string;
	value: number | undefined;
}

const paramLabel = (code: string) => `${PARAM_LABELS[code] ?? code}${PERCENT_CODES.includes(code) ? ' (%)' : ''}`;

const COLUMNS: ReferenceColumn<PlasteringRow>[] = [
	{
		key: 'param',
		header: 'Critério',
		render: (row) => <span className={tableStyles.cellPrimary}>{paramLabel(row.code)}</span>,
	},
	{
		key: 'value',
		header: 'Valor',
		render: (row) => <span className={tableStyles.cellValue}>{formatNumber(row.value)}</span>,
	},
];

const FIELDS = PLASTERING_CODES.map((code) => ({ key: code, label: paramLabel(code) }));

export const PlasteringSection = ({ reference, isSaving, onSave }: Omit<ReferenceSectionProps, 'onConfirmDelete'>) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const rows = useMemo<PlasteringRow[]>(
		() =>
			PLASTERING_CODES.map((code) => ({
				code,
				value: reference.plastering.find((item) => item.code === code)?.value,
			})),
		[reference.plastering]
	);
	const list = useTableList({ items: rows });

	return (
		<>
			<SectionList
				list={list}
				columns={COLUMNS}
				getRowId={(row) => row.code}
				unit={['critério', 'critérios']}
				onEdit={() => setIsModalOpen(true)}
			/>
			{isModalOpen && (
				<ValuesFormModal
					title="Editar critérios de gessagem"
					subtitle="Valores gerais da região, independentes do tipo de café."
					sectionTitle="Critérios"
					fields={FIELDS}
					initialValues={Object.fromEntries(rows.map((row) => [row.code, row.value]))}
					isSaving={isSaving}
					onSubmit={(values) =>
						onSave(
							{
								plastering: [
									...PLASTERING_CODES.map((code) => ({ code, value: values[code] })),
									...reference.plastering.filter((item) => !PLASTERING_CODES.includes(item.code)),
								],
							},
							'Gessagem salva com sucesso!'
						)
					}
					onClose={() => setIsModalOpen(false)}
				/>
			)}
		</>
	);
};
